import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useInRouterContext, useSearchParams } from 'react-router-dom'
import {
  type OwnedPrompt,
  ownedPromptsApi,
  type PromptCapabilities,
  type PromptFields,
  type PromptListQuery,
} from '../../api/owned-prompts'
import { getProjects } from '../../api/projects'
import type { ProjectResponse } from '../../types/api'
import { useDialog } from '../dialogs/useDialog'
import PromptCatalogDialog from './PromptCatalogDialog'
import PromptEditorDialog from './PromptEditorDialog'
import PromptMasterDetail from './PromptMasterDetail'

type Scope = 'all' | 'org' | number

interface PromptsWorkspaceProps {
  /** Fixed-project mode (project detail tab); omit for the org-wide prompt page. */
  projectId?: number
  /** Show the superadmin "Manage built-in catalog" link (admin page only). */
  showCatalogLink?: boolean
  title?: string
}

interface SelectionProps {
  selectedId: string | null
  onSelect: (id: string | null) => void
}

const buttonClass =
  'rounded-lg border border-harbor-border px-2.5 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50 hover:text-emerald-400'
const toolbarButtonClass =
  'rounded-lg border border-harbor-border px-3 py-2 text-sm font-medium text-harbor-text-secondary hover:bg-harbor-surface-raised'
const selectClass =
  'rounded-lg border border-harbor-border bg-harbor-surface-raised px-2.5 py-1.5 text-sm text-harbor-text'

function Workspace({
  projectId,
  showCatalogLink = false,
  title,
  selectedId,
  onSelect,
}: PromptsWorkspaceProps & SelectionProps) {
  const fixedMode = projectId !== undefined
  const [caps, setCaps] = useState<PromptCapabilities | null>(null)
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [scope, setScope] = useState<Scope | null>(projectId ?? null)
  const [prompts, setPrompts] = useState<OwnedPrompt[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<OwnedPrompt | 'new' | null>(null)
  const [newOwner, setNewOwner] = useState<'org' | number | null>(null)
  const [copyTarget, setCopyTarget] = useState<OwnedPrompt | null>(null)
  const [copyProjectId, setCopyProjectId] = useState<number | null>(null)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const dialog = useDialog()

  const projectNames = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p.name])) as Record<number, string>,
    [projects]
  )
  const scopeLabel = useCallback(
    (prompt: OwnedPrompt) => {
      if (prompt.scope.kind === 'builtin') return 'Built-in'
      if (prompt.scope.kind === 'organization') return 'Organization'
      return projectNames[prompt.scope.projectId] ?? `Project #${prompt.scope.projectId}`
    },
    [projectNames]
  )

  useEffect(() => {
    let cancelled = false
    Promise.all([ownedPromptsApi.capabilities(), fixedMode ? Promise.resolve([]) : getProjects()])
      .then(([c, p]) => {
        if (cancelled) return
        setCaps(c)
        setProjects(p)
        if (fixedMode) return
        // Admins (and superadmins) may list everything; others start on their first project.
        setScope(c.canImportBuiltin ? 'all' : (p[0]?.id ?? (c.canSeeOrg ? ('org' as const) : null)))
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load prompts')
      })
    return () => {
      cancelled = true
    }
  }, [fixedMode])

  const load = useCallback(async () => {
    if (scope === null) {
      setPrompts([])
      return
    }
    setError(null)
    try {
      const query: PromptListQuery =
        scope === 'all'
          ? { view: 'all' }
          : scope === 'org'
            ? { view: 'organization' }
            : { view: 'project', projectId: scope }
      setPrompts((await ownedPromptsApi.list(query)).items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load prompts')
    }
  }, [scope])

  useEffect(() => {
    if (caps?.mode === 'owned') void load()
  }, [caps, load])

  const authorable = useMemo(
    () =>
      caps === null
        ? []
        : fixedMode
          ? caps.canAuthorAllProjects || caps.managedProjectIds.includes(projectId)
            ? [projectId]
            : []
          : projects
              .filter((p) => caps.canAuthorAllProjects || caps.managedProjectIds.includes(p.id))
              .map((p) => p.id),
    [caps, fixedMode, projectId, projects]
  )

  function canEdit(prompt: OwnedPrompt): boolean {
    if (caps === null) return false
    if (prompt.scope.kind === 'organization') return caps.canEditOrg
    if (prompt.scope.kind === 'project')
      return caps.canAuthorAllProjects || caps.managedProjectIds.includes(prompt.scope.projectId)
    return false
  }
  function canCopy(prompt: OwnedPrompt): boolean {
    if (caps === null || authorable.length === 0) return false
    if (prompt.scope.kind === 'organization') return true
    return prompt.scope.kind === 'project' && caps.canAuthorAllProjects
  }

  async function handleSave(fields: PromptFields) {
    if (editing === 'new') {
      const owner = fixedMode ? projectId : newOwner
      await ownedPromptsApi.create(
        owner === 'org' || owner === null
          ? { kind: 'organization' }
          : { kind: 'project', projectId: owner },
        fields
      )
    } else if (editing) {
      await ownedPromptsApi.update(editing.id, fields, editing.updatedAt)
    }
    setEditing(null)
    setNewOwner(null)
    await load()
  }

  async function handleCopySave(fields: PromptFields) {
    if (!copyTarget || copyProjectId === null) return
    await ownedPromptsApi.copy(copyTarget.id, { kind: 'project', projectId: copyProjectId }, fields)
    setCopyTarget(null)
    setCopyProjectId(null)
    await load()
  }

  async function handleArchive(prompt: OwnedPrompt) {
    const confirmed = await dialog.confirm({
      title: `Delete "${prompt.title}"?`,
      description: 'This removes it from the library it is scoped to.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await ownedPromptsApi.archive(prompt.id)
      toast.success('Prompt deleted')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  function startNew() {
    const first: 'org' | number | null =
      typeof scope === 'number' && authorable.includes(scope)
        ? scope
        : caps?.canEditOrg
          ? 'org'
          : (authorable[0] ?? null)
    setNewOwner(first)
    setEditing('new')
  }

  if (error !== null) {
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-sm font-medium text-red-300">{error}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`mt-4 ${buttonClass}`}
        >
          Retry
        </button>
      </div>
    )
  }
  if (caps === null || (caps.mode === 'owned' && prompts === null)) {
    return (
      <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">Loading prompts…</p>
    )
  }
  if (caps.mode !== 'owned') {
    return (
      <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">
        {caps.mode === 'maintenance'
          ? 'Prompt library migration is in progress. Try again shortly.'
          : 'The prompt library has not been migrated for this organization yet.'}
      </p>
    )
  }

  const canCreate = authorable.length > 0 || (!fixedMode && caps.canEditOrg)
  const ownerOptions = [
    ...(caps.canEditOrg && !fixedMode ? [{ value: 'org' as const, label: 'Organization' }] : []),
    ...authorable.map((id) => ({ value: id, label: projectNames[id] ?? `Project #${id}` })),
  ]
  const heightClass = fixedMode ? 'h-[70vh] min-h-[420px]' : undefined

  return (
    <div className={fixedMode ? 'px-6 py-5' : 'mx-auto max-w-7xl px-6 py-8'}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {(title ?? !fixedMode) && (
          <h1 className="text-xl font-semibold text-harbor-text">{title ?? 'Prompt Library'}</h1>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {showCatalogLink && caps.canManageCatalog && (
            <Link to="/admin/builtin-prompts" className={toolbarButtonClass}>
              Manage built-in catalog
            </Link>
          )}
          {caps.canImportBuiltin && !fixedMode && (
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              className={toolbarButtonClass}
            >
              Import Prompts
            </button>
          )}
          {fixedMode && caps.canSeeOrg && authorable.length > 0 && (
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              className={toolbarButtonClass}
            >
              Copy from organization
            </button>
          )}
          {canCreate && (
            <button
              type="button"
              onClick={startNew}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
            >
              New prompt
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label htmlFor="prompts-scope" className="text-xs font-medium text-harbor-text-muted">
          Scope
        </label>
        <select
          id="prompts-scope"
          value={scope === null ? '' : String(scope)}
          onChange={(e) => {
            const v = e.target.value
            setScope(v === 'all' || v === 'org' ? v : Number(v))
            onSelect(null)
          }}
          className={selectClass}
        >
          {!fixedMode && caps.canImportBuiltin && <option value="all">All</option>}
          {caps.canSeeOrg && (
            <option value="org">{fixedMode ? 'Organization (read-only)' : 'Organization'}</option>
          )}
          {fixedMode ? (
            <option value={String(projectId)}>This project</option>
          ) : (
            projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))
          )}
        </select>
      </div>

      <div className="mt-4">
        <PromptMasterDetail
          prompts={prompts ?? []}
          scopeLabel={scopeLabel}
          selectedId={selectedId}
          onSelect={onSelect}
          heightClass={heightClass}
          emptyState={
            <p className="mt-10 text-center text-sm text-harbor-text-secondary">
              {scope === null
                ? 'You do not have access to any projects with prompts yet.'
                : canCreate
                  ? 'No prompts yet. Create one to get started.'
                  : 'No prompts found.'}
            </p>
          }
          renderActions={(prompt) => (
            <>
              {canEdit(prompt) && (
                <button type="button" onClick={() => setEditing(prompt)} className={buttonClass}>
                  Edit
                </button>
              )}
              {canCopy(prompt) && (
                <button type="button" onClick={() => setCopyTarget(prompt)} className={buttonClass}>
                  Copy to project
                </button>
              )}
              {canEdit(prompt) && (
                <button
                  type="button"
                  onClick={() => void handleArchive(prompt)}
                  className="rounded-lg border border-red-500/40 px-2.5 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/70"
                >
                  Delete
                </button>
              )}
              {!canEdit(prompt) && !canCopy(prompt) && (
                <span className="text-xs text-harbor-text-muted">Read-only</span>
              )}
            </>
          )}
        />
      </div>

      {editing !== null && (
        <div>
          {editing === 'new' && !fixedMode && ownerOptions.length > 1 && (
            <div className="fixed inset-x-0 top-4 z-[60] mx-auto w-fit rounded-lg border border-harbor-border bg-harbor-surface px-4 py-2 text-sm text-harbor-text shadow-lg">
              <label htmlFor="new-prompt-owner" className="mr-2 text-harbor-text-muted">
                Owner:
              </label>
              <select
                id="new-prompt-owner"
                value={String(newOwner)}
                onChange={(e) =>
                  setNewOwner(e.target.value === 'org' ? 'org' : Number(e.target.value))
                }
                className={selectClass}
              >
                {ownerOptions.map((o) => (
                  <option key={o.value} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <PromptEditorDialog
            title={editing === 'new' ? 'New prompt' : `Edit "${editing.title}"`}
            initial={
              editing === 'new'
                ? { title: '', description: null, body: '' }
                : { title: editing.title, description: editing.description, body: editing.body }
            }
            onSave={handleSave}
            onClose={() => {
              setEditing(null)
              setNewOwner(null)
            }}
          />
        </div>
      )}

      {copyTarget !== null && (
        <div>
          <div className="fixed inset-x-0 top-4 z-[60] mx-auto w-fit rounded-lg border border-harbor-border bg-harbor-surface px-4 py-2 text-sm text-harbor-text shadow-lg">
            <label htmlFor="copy-target-project" className="mr-2 text-harbor-text-muted">
              Copy to:
            </label>
            <select
              id="copy-target-project"
              value={copyProjectId ?? ''}
              onChange={(e) =>
                setCopyProjectId(e.target.value === '' ? null : Number(e.target.value))
              }
              className={selectClass}
            >
              <option value="">Select a project…</option>
              {authorable.map((id) => (
                <option key={id} value={id}>
                  {projectNames[id] ?? `Project #${id}`}
                </option>
              ))}
            </select>
          </div>
          {copyProjectId !== null && (
            <PromptEditorDialog
              title={`Copy "${copyTarget.title}"`}
              initial={{
                title: copyTarget.title,
                description: copyTarget.description,
                body: copyTarget.body,
              }}
              onSave={handleCopySave}
              onClose={() => {
                setCopyTarget(null)
                setCopyProjectId(null)
              }}
            />
          )}
        </div>
      )}

      {catalogOpen &&
        (fixedMode ? (
          <PromptCatalogDialog
            source="organization"
            projectId={projectId}
            onClose={() => setCatalogOpen(false)}
            onSaved={() => void load()}
          />
        ) : (
          <PromptCatalogDialog
            source="builtin"
            onClose={() => setCatalogOpen(false)}
            onSaved={() => void load()}
          />
        ))}
    </div>
  )
}

/** Selection lives in the URL (?prompt=<id>) so a prompt is linkable and survives refresh. */
function RoutedWorkspace(props: PromptsWorkspaceProps) {
  const [params, setParams] = useSearchParams()
  return (
    <Workspace
      {...props}
      selectedId={params.get('prompt')}
      onSelect={(id) =>
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            if (id === null) next.delete('prompt')
            else next.set('prompt', id)
            return next
          },
          { replace: true }
        )
      }
    />
  )
}

function LocalWorkspace(props: PromptsWorkspaceProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  return <Workspace {...props} selectedId={selectedId} onSelect={setSelectedId} />
}

export default function PromptsWorkspace(props: PromptsWorkspaceProps) {
  return useInRouterContext() ? <RoutedWorkspace {...props} /> : <LocalWorkspace {...props} />
}
